import {
  customElement,
  html,
  css,
  cssManager,
  property,
  state,
  type TemplateResult,
} from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-layout/dees-tile/dees-tile.js';
import { demoFunc } from './dees-input-list.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

export interface IListCandidate {
  viewKey: string;
  payload?: any;
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-list': DeesInputList;
  }
}

@customElement('dees-input-list')
export class DeesInputList extends DeesInputBase<DeesInputList> {
  public static inputLayout = 'compound' as const;
  // STATIC
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE
  @property({ type: Array })
  accessor value: string[] = [];

  @property({ type: String })
  accessor placeholder: string = 'Add new item...';

  @property({ type: Number })
  accessor maxItems: number = 0; // 0 means unlimited

  @property({ type: Number })
  accessor minItems: number = 0;

  @property({ type: Boolean })
  accessor allowDuplicates: boolean = false;

  @property({ type: Boolean })
  accessor sortable: boolean = false;

  @property({ type: Boolean })
  accessor confirmDelete: boolean = false;

  @property({ type: Array })
  accessor candidates: IListCandidate[] = [];

  @property({ type: Boolean })
  accessor allowFreeform: boolean = false;

  @property({ type: String })
  accessor validationText: string = '';

  /** Drop the list's outer frame: the host already owns the surface. */
  @property({ type: Boolean, reflect: true })
  accessor frameless: boolean = false;

  private addedCandidatesMap: Map<string, IListCandidate> = new Map();
  private matchingCandidates: IListCandidate[] = [];

  @state()
  accessor inputValue: string = '';

  @state()
  accessor ghostText: string = '';

  @state()
  accessor currentCandidateIndex: number = -1;

  @state()
  accessor editingIndex: number = -1;

  @state()
  accessor editingValue: string = '';

  @state()
  accessor draggedIndex: number = -1;

  @state()
  accessor dragOverIndex: number = -1;

  // Enhanced drag state for interactive reordering
  @state()
  accessor dragStartY: number = 0;

  @state()
  accessor dragCurrentY: number = 0;

  @state()
  accessor targetIndex: number = -1;

  @state()
  accessor itemHeight: number = 0;

  // Bound event handlers for cleanup
  private boundHandleGlobalDragOver: ((e: DragEvent) => void) | null = null;
  private boundHandleGlobalDragEnd: (() => void) | null = null;

  // Store original item positions for accurate hit detection (before transforms)
  private originalItemRects: DOMRect[] = [];

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      /* TODO: Migrate hardcoded values to --dees-* CSS variables */
      :host {
        display: block;
        font-family: var(--dees-font-family);
      }

      .input-wrapper {
        width: 100%;
      }

      dees-tile:hover::part(outer) {
        border-color: var(--dees-color-border-strong);
      }

      dees-tile:focus-within::part(outer) {
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: 1px;
        box-shadow: none;
      }

      :host([disabled]) dees-tile {
        opacity: 0.6;
        cursor: not-allowed;
        pointer-events: none;
      }

      .list-items {
        max-height: 400px;
        overflow-y: auto;
      }

      .list-item {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        border-bottom: 1px solid var(--dees-color-border-subtle);
        background: transparent;
        transition: transform 0.2s ease, background 0.15s ease, box-shadow 0.15s ease;
        position: relative;
        overflow: hidden;
      }

      .list-item:last-of-type {
        border-bottom: none;
      }

      .list-items:not(.is-dragging) .list-item:hover:not(.disabled) {
        background: var(--dees-color-row-hover);
      }

      /* Dragging item - follows cursor */
      .list-item.dragging {
        position: relative;
        z-index: 100;
        background: var(--dees-color-bg-primary);
        box-shadow: var(--dees-shadow-lg);
        border-radius: 6px;
        transition: box-shadow 0.15s ease, background 0.15s ease;
      }

      /* Items that need to move up to make space */
      .list-item.move-up {
        transform: translateY(calc(-1 * var(--item-height, 48px)));
      }

      /* Items that need to move down to make space */
      .list-item.move-down {
        transform: translateY(var(--item-height, 48px));
      }


      .candidate-check {
        width: 14px;
        height: 14px;
        color: var(--dees-color-text-success);
        flex-shrink: 0;
      }

      .candidate-unknown {
        width: 14px;
        height: 14px;
        color: var(--dees-color-text-warning);
        flex-shrink: 0;
      }

      .item-bullet {
        width: 14px;
        height: 14px;
        color: var(--dees-color-text-muted);
        flex-shrink: 0;
      }

      .drag-handle {
        display: flex;
        align-items: center;
        cursor: move;
        color: var(--dees-color-text-muted);
        transition: color 0.15s ease;
      }

      .drag-handle:hover {
        color: var(--dees-color-text-muted);
      }

      .drag-handle dees-icon {
        width: 14px;
        height: 14px;
      }

      .item-content {
        flex: 1;
        display: flex;
        align-items: center;
        min-width: 0;
      }

      .item-text {
        flex: 1;
        color: var(--dees-color-text-primary);
        font-size: 13px;
        line-height: 18px;
        word-break: break-word;
      }

      .item-edit-input {
        flex: 1;
        padding: 3px 6px;
        font-size: 13px;
        font-family: inherit;
        background: var(--dees-color-control-bg);
        border: 1px solid var(--dees-color-accent-primary);
        border-radius: 4px;
        outline: none;
        color: var(--dees-color-text-primary);
      }

      .item-actions {
        display: flex;
        gap: 4px;
        align-items: center;
      }

      .action-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--dees-input-action-size);
        height: var(--dees-input-action-size);
        border-radius: 4px;
        background: transparent;
        border: none;
        cursor: pointer;
        transition: all 0.15s ease;
        color: var(--dees-color-text-muted);
      }

      .action-button:hover {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-text-primary);
      }

      .action-button.save {
        color: var(--dees-color-text-success);
      }

      .action-button.save:hover {
        background: color-mix(in srgb, var(--dees-color-text-success) 10%, transparent);
      }

      .action-button.cancel {
        color: var(--dees-color-text-error);
      }

      .action-button.cancel:hover {
        background: color-mix(in srgb, var(--dees-color-text-error) 10%, transparent);
      }

      .action-button.delete {
        color: var(--dees-color-text-secondary);
      }

      .action-button.delete:hover {
        color: var(--dees-color-text-error);
        background: color-mix(in srgb, var(--dees-color-text-error) 10%, transparent);
      }

      .action-button dees-icon {
        width: 13px;
        height: 13px;
      }

      .add-item-container {
        display: flex;
        gap: 6px;
        padding: 6px 10px;
      }

      .add-input {
        width: 100%;
        padding: 4px 8px;
        font-size: 13px;
        line-height: 18px;
        font-family: inherit;
        background: transparent;
        border: none;
        outline: none;
        color: var(--dees-color-text-primary);
        min-width: 0;
      }

      .add-input::placeholder {
        color: var(--dees-color-text-muted);
      }

      .add-input:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }

      .add-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--dees-input-action-size);
        height: var(--dees-input-action-size);
        border-radius: 4px;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--dees-color-text-muted);
        transition: all 0.15s ease;
        flex-shrink: 0;
      }

      .add-button:hover:not(:disabled) {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-text-primary);
      }

      .add-button:disabled {
        opacity: 0.3;
        cursor: default;
      }

      .add-button dees-icon {
        width: 14px;
        height: 14px;
      }

      .empty-state {
        padding: 16px 10px;
        text-align: center;
        color: var(--dees-color-text-muted);
        font-size: 13px;
      }

      .validation-message {
        color: var(--dees-color-accent-error);
        font-size: 12px;
        margin-top: 4px;
        line-height: 1.4;
      }

      /* Scrollbar styling */
      .list-items::-webkit-scrollbar {
        width: 8px;
      }

      .list-items::-webkit-scrollbar-track {
        background: transparent;
      }

      .list-items::-webkit-scrollbar-thumb {
        background: var(--dees-color-border-default);
        border-radius: 4px;
      }

      .list-items::-webkit-scrollbar-thumb:hover {
        background: var(--dees-color-border-strong);
      }

      /* Disable transitions during drop to prevent flash */
      .list-items.dropping .list-item {
        transition: none !important;
      }

      /* ── Terminal-style inline autocomplete ── */
      .autocomplete-wrapper {
        position: relative;
        flex: 1;
        min-width: 0;
        overflow: hidden;
      }

      .ghost-text {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        padding: 4px 8px;
        font-size: 13px;
        line-height: 18px;
        font-family: inherit;
        white-space: nowrap;
        pointer-events: none;
        overflow: hidden;
      }

      .ghost-typed {
        visibility: hidden;
      }

      .ghost-completion {
        color: var(--dees-color-label-quaternary);
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        ${this.label ? this.renderLabel() : ''}
        
        <dees-tile .heading="${this.value.length} item${this.value.length !== 1 ? 's' : ''}" .frameless=${this.frameless}>
          <div class="list-items">
            ${this.value.length > 0 ? this.value.map((item, index) => html`
              <div
                class="list-item ${this.draggedIndex === index ? 'dragging' : ''}"
                draggable="${this.sortable && !this.disabled}"
                @dragstart=${(e: DragEvent) => this.handleDragStart(e, index)}
                @dragend=${this.handleDragEnd}
                @dragover=${(e: DragEvent) => this.handleDragOver(e, index)}
                @drop=${(e: DragEvent) => this.handleDrop(e, index)}
              >
                ${this.sortable && !this.disabled ? html`
                  <div class="drag-handle">
                    <dees-icon .icon=${'lucide:gripVertical'}></dees-icon>
                  </div>
                ` : ''}

                ${this.candidates.length > 0 ? html`
                  ${this.candidates.some(c => c.viewKey === item) ? html`
                    <dees-icon class="candidate-check" .icon=${'lucide:check'}></dees-icon>
                  ` : html`
                    <dees-icon class="candidate-unknown" .icon=${'lucide:helpCircle'}></dees-icon>
                  `}
                ` : !this.sortable || this.disabled ? html`
                  <dees-icon class="item-bullet" .icon=${'lucide:dot'}></dees-icon>
                ` : ''}

                <div class="item-content">
                  ${this.editingIndex === index ? html`
                    <input
                      type="text"
                      class="item-edit-input"
                      .value=${this.editingValue}
                      @input=${(e: InputEvent) => this.editingValue = (e.target as HTMLInputElement).value}
                      @keydown=${(e: KeyboardEvent) => this.handleEditKeyDown(e, index)}
                      @blur=${() => this.saveEdit(index)}
                    />
                  ` : html`
                    <div class="item-text" @dblclick=${() => !this.disabled && this.startEdit(index)}>
                      ${item}
                    </div>
                  `}
                </div>
                
                <div class="item-actions">
                  ${this.editingIndex === index ? html`
                    <button class="action-button save" @click=${() => this.saveEdit(index)}>
                      <dees-icon .icon=${'lucide:check'}></dees-icon>
                    </button>
                    <button class="action-button cancel" @click=${() => this.cancelEdit()}>
                      <dees-icon .icon=${'lucide:x'}></dees-icon>
                    </button>
                  ` : html`
                    ${!this.disabled ? html`
                      <button class="action-button" @click=${() => this.startEdit(index)}>
                        <dees-icon .icon=${'lucide:pencil'}></dees-icon>
                      </button>
                      <button class="action-button delete" @click=${() => this.removeItem(index)}>
                        <dees-icon .icon=${'lucide:trash2'}></dees-icon>
                      </button>
                    ` : ''}
                  `}
                </div>
              </div>
            `) : html`
              <div class="empty-state">
                No items added yet
              </div>
            `}
          </div>
          
          ${!this.disabled && (!this.maxItems || this.value.length < this.maxItems) ? html`
            <div slot="footer" class="add-item-container">
              <div class="autocomplete-wrapper">
                ${this.ghostText ? html`
                  <span class="ghost-text">
                    <span class="ghost-typed">${this.inputValue}</span><span class="ghost-completion">${this.ghostText}</span>
                  </span>
                ` : ''}
                <input
                  type="text"
                  class="add-input"
                  .placeholder=${this.placeholder}
                  .value=${this.inputValue}
                  @input=${this.handleInput}
                  @keydown=${this.handleAddKeyDown}
                  ?disabled=${this.disabled}
                />
              </div>
              <button
                class="add-button"
                @click=${this.addItem}
                ?disabled=${!this.inputValue.trim()}
              >
                <dees-icon .icon=${'lucide:plus'}></dees-icon>
              </button>
            </div>
          ` : ''}
        </dees-tile>

        ${this.validationText ? html`
          <div class="validation-message">${this.validationText}</div>
        ` : ''}
        
        ${this.renderDescription()}
      </div>
    `;
  }

  private handleInput(e: InputEvent) {
    this.inputValue = (e.target as HTMLInputElement).value;
    this.updateGhostText();
  }

  private updateGhostText(): void {
    if (this.candidates.length === 0 || !this.inputValue) {
      this.ghostText = '';
      this.currentCandidateIndex = -1;
      this.matchingCandidates = [];
      return;
    }

    const search = this.inputValue.toLowerCase();
    this.matchingCandidates = this.candidates
      .filter(c => {
        if (this.value.includes(c.viewKey)) return false;
        return c.viewKey.toLowerCase().startsWith(search);
      })
      .sort((a, b) => a.viewKey.length - b.viewKey.length);

    if (this.matchingCandidates.length > 0) {
      this.currentCandidateIndex = 0;
      this.ghostText = this.matchingCandidates[0].viewKey.slice(this.inputValue.length);
    } else {
      this.currentCandidateIndex = -1;
      this.ghostText = '';
    }
  }

  private handleAddKeyDown(e: KeyboardEvent) {
    // Tab/Shift+Tab: autocomplete handling when candidates are active
    if (e.key === 'Tab' && this.candidates.length > 0 && this.inputValue) {
      e.preventDefault();
      if (e.shiftKey && this.matchingCandidates.length > 0) {
        // Shift+Tab: cycle to next candidate
        this.currentCandidateIndex = (this.currentCandidateIndex + 1) % this.matchingCandidates.length;
        const candidate = this.matchingCandidates[this.currentCandidateIndex];
        this.ghostText = candidate.viewKey.slice(this.inputValue.length);
      } else if (!e.shiftKey && this.ghostText && this.matchingCandidates.length > 0) {
        // Tab: accept the completion into the input
        const candidate = this.matchingCandidates[this.currentCandidateIndex];
        this.inputValue = candidate.viewKey;
        this.ghostText = '';
        const input = this.shadowRoot?.querySelector('.add-input') as HTMLInputElement;
        if (input) input.value = candidate.viewKey;
      }
      return;
    }

    // Escape: clear ghost text
    if (e.key === 'Escape' && this.ghostText) {
      e.preventDefault();
      this.ghostText = '';
      this.currentCandidateIndex = -1;
      this.matchingCandidates = [];
      return;
    }

    // Enter: add item
    if (e.key === 'Enter' && this.inputValue.trim()) {
      e.preventDefault();
      if (this.candidates.length > 0) {
        // Try exact candidate match first
        const match = this.candidates.find(
          c => c.viewKey.toLowerCase() === this.inputValue.trim().toLowerCase()
        );
        if (match) {
          this.selectCandidate(match);
        } else if (this.allowFreeform) {
          // Allow freeform entry (won't have a candidate checkmark)
          this.ghostText = '';
          this.currentCandidateIndex = -1;
          this.matchingCandidates = [];
          this.addItem();
        }
        return;
      }
      this.addItem();
    }
  }

  private handleEditKeyDown(e: KeyboardEvent, index: number) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.saveEdit(index);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.cancelEdit();
    }
  }

  private selectCandidate(candidate: IListCandidate): void {
    if (this.maxItems && this.value.length >= this.maxItems) {
      this.validationText = `Maximum ${this.maxItems} items allowed`;
      setTimeout(() => this.validationText = '', 3000);
      return;
    }

    if (!this.allowDuplicates && this.value.includes(candidate.viewKey)) {
      this.validationText = 'This item already exists in the list';
      setTimeout(() => this.validationText = '', 3000);
      return;
    }

    this.addedCandidatesMap.set(candidate.viewKey, candidate);
    this.value = [...this.value, candidate.viewKey];
    this.inputValue = '';
    this.ghostText = '';
    this.currentCandidateIndex = -1;
    this.matchingCandidates = [];
    this.validationText = '';
    this.emitChange();

    // Re-focus input after Lit re-renders
    this.updateComplete.then(() => {
      const input = this.shadowRoot?.querySelector('.add-input') as HTMLInputElement;
      if (input) { input.value = ''; input.focus(); }
    });
  }

  /**
   * Get the full candidate object for an item by its viewKey.
   * Returns undefined if the item was added as a plain string.
   */
  public getCandidateForItem(viewKey: string): IListCandidate | undefined {
    return this.addedCandidatesMap.get(viewKey);
  }

  /**
   * Get all added candidates with their payloads.
   */
  public getAddedCandidates(): IListCandidate[] {
    return this.value
      .map(v => this.addedCandidatesMap.get(v))
      .filter((c): c is IListCandidate => c !== undefined);
  }

  private addItem() {
    const trimmedValue = this.inputValue.trim();
    if (!trimmedValue) return;

    if (!this.allowDuplicates && this.value.includes(trimmedValue)) {
      this.validationText = 'This item already exists in the list';
      setTimeout(() => this.validationText = '', 3000);
      return;
    }

    if (this.maxItems && this.value.length >= this.maxItems) {
      this.validationText = `Maximum ${this.maxItems} items allowed`;
      setTimeout(() => this.validationText = '', 3000);
      return;
    }

    this.value = [...this.value, trimmedValue];
    this.inputValue = '';
    this.validationText = '';
    this.emitChange();

    // Re-focus input after Lit re-renders
    this.updateComplete.then(() => {
      const input = this.shadowRoot?.querySelector('.add-input') as HTMLInputElement;
      if (input) { input.value = ''; input.focus(); }
    });
  }

  private startEdit(index: number) {
    this.editingIndex = index;
    this.editingValue = this.value[index];
    
    // Focus the input after render
    this.updateComplete.then(() => {
      const input = this.shadowRoot?.querySelector('.item-edit-input') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  private saveEdit(index: number) {
    const trimmedValue = this.editingValue.trim();
    
    if (!trimmedValue) {
      this.cancelEdit();
      return;
    }

    if (!this.allowDuplicates && trimmedValue !== this.value[index] && this.value.includes(trimmedValue)) {
      this.validationText = 'This item already exists in the list';
      setTimeout(() => this.validationText = '', 3000);
      return;
    }

    const newValue = [...this.value];
    newValue[index] = trimmedValue;
    this.value = newValue;
    
    this.editingIndex = -1;
    this.editingValue = '';
    this.validationText = '';
    this.emitChange();
  }

  private cancelEdit() {
    this.editingIndex = -1;
    this.editingValue = '';
  }

  private async removeItem(index: number) {
    if (this.confirmDelete) {
      const confirmed = await this.showConfirmDialog(`Delete "${this.value[index]}"?`);
      if (!confirmed) return;
    }

    const removedKey = this.value[index];
    this.addedCandidatesMap.delete(removedKey);
    this.value = this.value.filter((_, i) => i !== index);
    this.emitChange();
  }

  private async showConfirmDialog(message: string): Promise<boolean> {
    // For now, use native confirm. In production, this should use a proper modal
    return confirm(message);
  }

  // Drag and drop handlers - Interactive implementation
  private handleDragStart(e: DragEvent, index: number) {
    if (!this.sortable || this.disabled) return;

    this.draggedIndex = index;
    this.targetIndex = index;
    e.dataTransfer!.effectAllowed = 'move';
    e.dataTransfer!.setData('text/plain', index.toString());

    // Hide the default drag image
    const emptyImg = new Image();
    emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
    e.dataTransfer!.setDragImage(emptyImg, 0, 0);

    // Store initial mouse position
    this.dragStartY = e.clientY;
    this.dragCurrentY = e.clientY;

    // Measure item height and store all original positions before any transforms
    const listItems = this.shadowRoot?.querySelector('.list-items');
    const allItems = Array.from(listItems?.querySelectorAll('.list-item') || []) as HTMLElement[];

    if (allItems[index]) {
      this.itemHeight = allItems[index].offsetHeight;
    }

    // Store original positions for accurate hit detection (before any transforms are applied)
    this.originalItemRects = allItems.map(item => item.getBoundingClientRect());

    // Add class to container
    listItems?.classList.add('is-dragging');

    // Set up global event listeners
    this.boundHandleGlobalDragOver = this.handleGlobalDragOver.bind(this);
    this.boundHandleGlobalDragEnd = this.handleGlobalDragEnd.bind(this);
    document.addEventListener('dragover', this.boundHandleGlobalDragOver);
    document.addEventListener('dragend', this.boundHandleGlobalDragEnd);
  }

  private handleGlobalDragOver(e: DragEvent) {
    e.preventDefault();
    if (this.draggedIndex === -1) return;

    this.dragCurrentY = e.clientY;

    // Calculate which position the item should move to
    const listItems = this.shadowRoot?.querySelector('.list-items');
    if (!listItems) return;

    const items = Array.from(listItems.querySelectorAll('.list-item')) as HTMLElement[];
    const draggedElement = items[this.draggedIndex];
    if (!draggedElement) return;

    // Apply transform to dragged item
    const deltaY = this.dragCurrentY - this.dragStartY;
    draggedElement.style.transform = `translateY(${deltaY}px)`;

    // Calculate the dragged item's current center position
    const draggedRect = this.originalItemRects[this.draggedIndex];
    if (!draggedRect) return;
    const draggedCenter = draggedRect.top + draggedRect.height / 2 + deltaY;

    // Determine target index: swap when dragged item's center crosses another item's center
    // Account for items that have already shifted (their visual position changed)
    let newTargetIndex = this.draggedIndex;

    for (let i = 0; i < items.length; i++) {
      if (i === this.draggedIndex) continue;

      const rect = this.originalItemRects[i];
      if (!rect) continue;

      // Adjust item center based on whether it has shifted
      let itemCenter = rect.top + rect.height / 2;

      // If item has moved, use its shifted position
      if (items[i].classList.contains('move-up')) {
        itemCenter -= this.itemHeight;
      } else if (items[i].classList.contains('move-down')) {
        itemCenter += this.itemHeight;
      }

      if (draggedCenter < itemCenter && i < this.draggedIndex) {
        newTargetIndex = i;
        break;
      } else if (draggedCenter > itemCenter && i > this.draggedIndex) {
        newTargetIndex = i;
      }
    }

    // Update target index and apply move classes
    if (newTargetIndex !== this.targetIndex) {
      this.targetIndex = newTargetIndex;
      this.updateItemPositions(items);
    }
  }

  private updateItemPositions(items: HTMLElement[]) {
    const draggedIdx = this.draggedIndex;
    const targetIdx = this.targetIndex;

    // Set CSS variable for item height
    const listItems = this.shadowRoot?.querySelector('.list-items') as HTMLElement;
    if (listItems) {
      listItems.style.setProperty('--item-height', `${this.itemHeight}px`);
    }

    items.forEach((item, i) => {
      if (i === draggedIdx) return; // Skip dragged item

      item.classList.remove('move-up', 'move-down');
      item.style.setProperty('--item-height', `${this.itemHeight}px`);

      if (draggedIdx < targetIdx) {
        // Dragging down: items between draggedIdx and targetIdx move up
        if (i > draggedIdx && i <= targetIdx) {
          item.classList.add('move-up');
        }
      } else if (draggedIdx > targetIdx) {
        // Dragging up: items between targetIdx and draggedIdx move down
        if (i >= targetIdx && i < draggedIdx) {
          item.classList.add('move-down');
        }
      }
    });
  }

  private handleGlobalDragEnd() {
    // Clean up event listeners
    if (this.boundHandleGlobalDragOver) {
      document.removeEventListener('dragover', this.boundHandleGlobalDragOver);
      this.boundHandleGlobalDragOver = null;
    }
    if (this.boundHandleGlobalDragEnd) {
      document.removeEventListener('dragend', this.boundHandleGlobalDragEnd);
      this.boundHandleGlobalDragEnd = null;
    }

    const listItems = this.shadowRoot?.querySelector('.list-items');
    const items = listItems?.querySelectorAll('.list-item') as NodeListOf<HTMLElement>;
    const draggedElement = items?.[this.draggedIndex];

    // If no reorder needed, animate back and clean up
    if (this.draggedIndex === -1 || this.targetIndex === -1 || this.draggedIndex === this.targetIndex) {
      // Animate dragged item back to original position
      if (draggedElement && this.draggedIndex !== -1) {
        draggedElement.style.transition = 'transform 0.15s ease';
        draggedElement.style.transform = 'translateY(0)';

        let handled = false;
        const onReturn = () => {
          if (handled) return;
          handled = true;
          draggedElement.removeEventListener('transitionend', onReturn);
          this.cleanupDragState(listItems, items);
        };

        draggedElement.addEventListener('transitionend', onReturn, { once: true });
        setTimeout(onReturn, 200);
      } else {
        this.cleanupDragState(listItems, items);
      }
      return;
    }

    // Calculate final position for dragged item
    const draggedRect = this.originalItemRects[this.draggedIndex];
    const targetRect = this.originalItemRects[this.targetIndex];

    if (!draggedRect || !targetRect || !draggedElement) {
      this.cleanupDragState(listItems, items);
      return;
    }

    // Calculate where dragged item needs to go
    let finalY: number;
    if (this.targetIndex > this.draggedIndex) {
      // Moving down: go to bottom of target
      finalY = targetRect.bottom - draggedRect.bottom;
    } else {
      // Moving up: go to top of target
      finalY = targetRect.top - draggedRect.top;
    }

    // Animate dragged item to final position
    draggedElement.style.transition = 'transform 0.15s ease';
    draggedElement.style.transform = `translateY(${finalY}px)`;

    // After animation completes, update data
    let handled = false;
    const onTransitionEnd = () => {
      if (handled) return;
      handled = true;
      draggedElement.removeEventListener('transitionend', onTransitionEnd);

      // Disable all transitions
      listItems?.classList.add('dropping');

      // Force reflow so dropping class takes effect immediately
      void (listItems as HTMLElement)?.offsetHeight;

      // Clean up all element state
      items?.forEach(item => {
        item.classList.remove('move-up', 'move-down', 'dragging');
        item.style.removeProperty('transform');
        item.style.removeProperty('transition');
      });

      // Update data
      const newValue = [...this.value];
      const [draggedItem] = newValue.splice(this.draggedIndex, 1);
      newValue.splice(this.targetIndex, 0, draggedItem);
      this.value = newValue;
      this.emitChange();

      // Reset state
      this.draggedIndex = -1;
      this.dragOverIndex = -1;
      this.targetIndex = -1;
      this.dragStartY = 0;
      this.dragCurrentY = 0;
      this.originalItemRects = [];

      // After render, ensure no animation then re-enable transitions
      this.updateComplete.then(() => {
        // Set inline transition:none on fresh elements
        const freshItems = this.shadowRoot?.querySelectorAll('.list-item') as NodeListOf<HTMLElement>;
        freshItems?.forEach(item => {
          item.style.transition = 'none';
        });

        // Force reflow
        void (this.shadowRoot?.querySelector('.list-items') as HTMLElement)?.offsetHeight;

        // Now re-enable transitions
        requestAnimationFrame(() => {
          freshItems?.forEach(item => {
            item.style.removeProperty('transition');
          });
          listItems?.classList.remove('dropping', 'is-dragging');
        });
      });
    };

    draggedElement.addEventListener('transitionend', onTransitionEnd, { once: true });

    // Fallback timeout in case transitionend doesn't fire
    setTimeout(onTransitionEnd, 200);
  }

  private cleanupDragState(listItems: Element | null | undefined, items: NodeListOf<HTMLElement> | undefined) {
    listItems?.classList.add('dropping');

    // Force reflow so dropping class takes effect immediately
    void (listItems as HTMLElement)?.offsetHeight;

    items?.forEach(item => {
      item.classList.remove('move-up', 'move-down', 'dragging');
      item.style.removeProperty('transform');
      item.style.removeProperty('transition');
    });

    this.draggedIndex = -1;
    this.dragOverIndex = -1;
    this.targetIndex = -1;
    this.dragStartY = 0;
    this.dragCurrentY = 0;
    this.originalItemRects = [];

    this.updateComplete.then(() => {
      const freshItems = this.shadowRoot?.querySelectorAll('.list-item') as NodeListOf<HTMLElement>;
      freshItems?.forEach(item => {
        item.style.transition = 'none';
      });

      void (this.shadowRoot?.querySelector('.list-items') as HTMLElement)?.offsetHeight;

      requestAnimationFrame(() => {
        freshItems?.forEach(item => {
          item.style.removeProperty('transition');
        });
        listItems?.classList.remove('dropping', 'is-dragging');
      });
    });
  }

  private handleDragEnd() {
    // This is called by the native dragend on the element
    // The actual cleanup is done in handleGlobalDragEnd
    this.handleGlobalDragEnd();
  }

  private handleDragOver(e: DragEvent, index: number) {
    if (!this.sortable || this.disabled) return;
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'move';
    // We handle positioning in handleGlobalDragOver now
  }

  private handleDragLeave() {
    // No longer needed for visual feedback - handled by transform
  }

  private handleDrop(e: DragEvent, dropIndex: number) {
    if (!this.sortable || this.disabled) return;
    e.preventDefault();
    // The actual reorder happens in handleGlobalDragEnd
  }

  private emitChange() {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: this.value },
      bubbles: true,
      composed: true
    }));
    this.changeSubject.next(this);
  }

  public getValue(): string[] {
    return this.value;
  }

  public setValue(value: string[]): void {
    this.value = value || [];
  }

  public async validate(): Promise<boolean> {
    if (this.required && (!this.value || this.value.length === 0)) {
      this.validationText = 'At least one item is required';
      return false;
    }
    
    if (this.minItems && this.value.length < this.minItems) {
      this.validationText = `At least ${this.minItems} items required`;
      return false;
    }
    
    this.validationText = '';
    return true;
  }
}
